import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { faBell } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';

import { call } from '@spacefill/shared/src/utils/Backend';
import { useAnnouncementsContext } from '@spacefill/shared/src/utils/contexts/AnnouncementsContext';
import { ButtonText } from '@spacefill/uikit/src/components/ButtonText/ButtonText';

import { NotificationsSidePanel } from './Notifications/NotificationsSidePanel';

const ICON_BADGE_CAP = 99;

function formatCount(value, cap) {
    if (value <= 0 || Number.isNaN(value)) return '';
    return value > cap ? `${cap}+` : String(value);
}

function IconBadge({ value }) {
    if (value <= 0) return null;
    return (
        <div className='absolute -right-1.5 -top-1.5'>
            <div className='inline-flex min-w-4 items-center justify-center rounded-lg px-1 py-0.5 text-[10px] font-semibold bg-red-500 text-white leading-none'>
                {formatCount(value, ICON_BADGE_CAP)}
            </div>
        </div>
    );
}

export function TopBarActionsAndNotifications() {
    const { t } = useTranslation();
    const { openSidePanel: openAnnouncementsSidePanel } = useAnnouncementsContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const { pathname } = useLocation();

    // Synchroniser l'état du panel avec le paramètre d'URL
    const isNotificationsOpen = searchParams.has('notifications') || pathname === '/notifications';

    const handleNotificationsToggle = (open) => {
        if (open) {
            searchParams.set('notifications', '');
        } else {
            searchParams.delete('notifications');
        }
        setSearchParams(searchParams, { replace: true });
    };

    const { data: unreadCount = 0, isFetching: isFetchingNotifications } = useQuery({
        queryKey: ['/in-app-notifications/unread'],
        queryFn: () => call('/in-app-notifications/unread'),
        select: (d) => d?.count ?? 0,
        initialData: 0,
        refetchOnWindowFocus: true,
    });

    return (
        <div className='flex gap-2'>
            <ButtonText
                variant='secondary'
                onClick={openAnnouncementsSidePanel}
            >
                {t('Annoucements')}
            </ButtonText>

            <div className='relative'>
                <button
                    onClick={() => handleNotificationsToggle(true)}
                    className={clsx(
                        'relative flex h-8 w-8 items-center justify-center text-gray-500 transition-all hover:cursor-pointer hover:text-gray-700',
                        {
                            'text-gray-700': isNotificationsOpen,
                        }
                    )}
                >
                    <FontAwesomeIcon
                        icon={faBell}
                        className='text-xl'
                    />
                    {!isFetchingNotifications && <IconBadge value={unreadCount} />}
                </button>

                <NotificationsSidePanel
                    isOpen={isNotificationsOpen}
                    onClose={() => handleNotificationsToggle(false)}
                    unreadCount={unreadCount}
                />
            </div>
        </div>
    );
}
